/**
 * Presentation helpers shared by every surface that renders a payment —
 * PaymentDetailPage, the payment lists, and the seven callers of
 * PaymentQuickCreateDialog.
 *
 * They all render the same immutable financial record, so the allocation link
 * resolution, the payment-method fallback and the "which record did this
 * become?" naming live here rather than being copied per view — a divergence
 * would show a customer two different labels for the same payment.
 */

import type { ResolvedEntity } from '../../../shared/types';
import type { PaymentAllocation, StoredPaymentType } from '../types';

/**
 * Allocation target → entity table for a clickable EntityChip. Credit notes have
 * no standalone number/route, so they fall back to the plain type label.
 */
export const ALLOCATION_ENTITY_TABLE: Record<string, string> = {
  bill: 'accounting_bills',
  invoice: 'accounting_invoices',
};

/**
 * A clickable entity for an allocation's target document, or null when it can't
 * be linked (unknown type, or no resolved document number).
 */
export function allocationEntity(alloc: PaymentAllocation): ResolvedEntity | null {
  const table = ALLOCATION_ENTITY_TABLE[alloc.allocated_to_type];
  if (!table || !alloc.allocated_to_number) return null;
  return {
    entity_ref: `${table}:${alloc.allocated_to_id}`,
    entity_type: table,
    entity_id: alloc.allocated_to_id,
    title: alloc.allocated_to_number,
  };
}

/**
 * The receipt/payment side of a payment's identity, in ONE place.
 *
 * A `received` payment is a Receipt (REC-…) and lives on /accounting/receipts;
 * a `made` payment is a Payment (PAY-…) and lives on /accounting/payments. One
 * route and one dialog serve both, so every surface that has to name the record
 * — a breadcrumb parent, a success toast — derives it from here instead of
 * re-deciding, which is how "Record payment" / "Create Payment" / "Receipt"
 * became three names for one action.
 *
 * This is the WHICH-RECORD question, deliberately NOT the money-direction one —
 * see `isMoneyInPaymentType`. A `vendor_refund` is cash coming IN and is still a
 * vendor/AP record filed under Payments, so the two answers differ for it, and a
 * single predicate answering both would send its breadcrumb to a list it can
 * never appear on.
 */
export function isReceiptPaymentType(paymentType: StoredPaymentType | undefined): boolean {
  return paymentType === 'received';
}

/**
 * Which way the CASH moved: `received` (a customer paying us) and `vendor_refund`
 * (a vendor refunding a credit note, DR bank / CR AP) are money IN; `made`,
 * `refund` (a customer refund out) and `nsf_reversal` are money OUT.
 *
 * Direction and record identity are separate questions and only agree for three
 * of the five types, so every direction-dependent affordance — the Download vs
 * Upload glyph, "Deposited To" vs "Paid From" — reads THIS one. Getting it from
 * `isReceiptPaymentType` renders a vendor refund as money leaving the bank, which
 * is the exact error its backend fix exists to correct, one layer up.
 */
export function isMoneyInPaymentType(paymentType: StoredPaymentType | undefined): boolean {
  return paymentType === 'received' || paymentType === 'vendor_refund';
}

/**
 * i18n key for the list a payment of this type belongs to, and the route that
 * list is served at — the detail page's breadcrumb parent.
 */
export function paymentListCrumb(paymentType: StoredPaymentType | undefined): {
  labelKey: string;
  href: string;
} {
  return isReceiptPaymentType(paymentType)
    ? { labelKey: 'accounting:receipts.title', href: '/accounting/receipts' }
    : { labelKey: 'accounting:payments.title', href: '/accounting/payments' };
}

/**
 * i18n key for the "recorded successfully" toast, named for the record the
 * payment BECOMES. Returns a KEY rather than a string so the caller translates
 * it — a display helper in a plain `.ts` must never return English
 * (`.claude/rules/rules-frontend.md` § display-string helpers).
 */
export function paymentCreatedMessageKey(paymentType: StoredPaymentType | undefined): string {
  return isReceiptPaymentType(paymentType)
    ? 'accounting:receipts.createSuccess'
    : 'accounting:payments.createSuccess';
}

/** Title-case a raw enum token: "bank_transfer" → "Bank Transfer". */
export function humanizeToken(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
